/**
 * EntrancePage — App-style icon grid hub.
 * The default "/" route for all visitors.
 *
 * Tiles:
 *   Browse Shops → /browse (public, no auth)
 *   Shop Map     → /map    (public, no auth)
 *   My Shops     → /shops  (auth-gated)
 *   My Orders    → /orders (auth-gated)
 *   Scan         → /scan   (auth-gated)
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../stores/authStore'
import { useShopStore } from '../../stores/shopStore'
import { useInstallPrompt } from '../../hooks/useInstallPrompt'
import { useHomePageData } from '../../hooks/useHomePageData'
import { apiUrl } from '../../config'
import { getWishlistHistory, removeWishlistToken, type WishlistHistoryEntry } from '../../utils/wishlistHistory'
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher'
import ShopCardCarousel from './ShopCardCarousel'
import BrowseShopsInline from './BrowseShopsInline'
import './EntrancePage.css'

const tileKeys = [
  { id: 'browse',  labelKey: 'browseShops', icon: '🛍️', path: '/browse', requiresAuth: false },
  { id: 'map',     labelKey: 'shopMap',     icon: '🗺️', path: '/map',    requiresAuth: false },
  { id: 'shops',   labelKey: 'myShops',     icon: '🏪', path: '/shops',  requiresAuth: true  },
  { id: 'orders',  labelKey: 'myOrders',    icon: '📦', path: '/orders', requiresAuth: true  },
  { id: 'scan',    labelKey: 'scan',        icon: '📷', path: '/scan',   requiresAuth: true  },
  { id: 'admin',   labelKey: 'adminPanel',  icon: '🛡️', path: '/admin',  requiresAuth: true, requiresSuperuser: true },
]

export default function EntrancePage() {
  const { t } = useTranslation('entrance')
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.token)
  const setAuth = useAuthStore((s) => s.setAuth)
  const [restoring, setRestoring] = useState(true)
  const { showBanner, canPrompt, showIosHint, promptInstall, dismiss } = useInstallPrompt()
  const [wishlists, setWishlists] = useState<WishlistHistoryEntry[]>([])
  const { recentShops, recommendations, trending, loading: homeLoading } = useHomePageData()

  // Load wishlist history from localStorage
  useEffect(() => {
    setWishlists(getWishlistHistory())
  }, [])

  const handleRemoveWishlist = (token: string) => {
    removeWishlistToken(token)
    setWishlists((prev) => prev.filter((w) => w.token !== token))
  }

  // Restore session from localStorage (same logic as AuthGate)
  useEffect(() => {
    if (token || user) {
      setRestoring(false)
      return
    }
    const stored = localStorage.getItem('dukkan_token')
    if (!stored) {
      setRestoring(false)
      return
    }
    fetch(apiUrl('/api/auth/me'), { headers: { Authorization: `Bearer ${stored}` } })
      .then((res) => {
        if (res.ok) return res.json()
        throw new Error('expired')
      })
      .then((u) => {
        setAuth(u, stored)
        setRestoring(false)
      })
      .catch(() => {
        localStorage.removeItem('dukkan_token')
        setRestoring(false)
      })
  }, [])

  const { shops, fetchShops: fetchUserShops } = useShopStore()

  const handleTile = async (tile: typeof tileKeys[0]) => {
    if (tile.requiresAuth && !user) {
      // Store redirect target so after login we come back
      localStorage.setItem('dukkan_redirect_after_login', tile.path)
      navigate('/login')
      return
    }

    // My Shops: if user has exactly 1 shop, go straight to its dashboard
    if (tile.id === 'shops') {
      let userShops = shops
      if (userShops.length === 0) {
        await fetchUserShops()
        userShops = useShopStore.getState().shops
      }
      if (userShops.length === 1) {
        navigate(`/dashboard/${userShops[0].id}`)
        return
      }
    }

    navigate(tile.path)
  }

  return (
    <div className="entrance-page">
      {/* Top bar */}
      <header className="entrance-header">
        <h1 className="entrance-logo">{t('common:dukkan')}</h1>
        <div className="entrance-header-actions">
          <LanguageSwitcher />
          {user ? (
            <button className="entrance-avatar-btn" onClick={() => navigate('/profile')}>
              {user.picture ? (
                <img src={user.picture} alt="" className="entrance-avatar" referrerPolicy="no-referrer" />
              ) : (
                <span className="entrance-avatar-fallback">{user.name?.charAt(0) || '?'}</span>
              )}
            </button>
          ) : (
            <button className="entrance-sign-in" onClick={() => navigate('/login')}>
              {t('common:signIn')}
            </button>
          )}
        </div>
      </header>

      {/* PWA Install Banner */}
      {showBanner && (
        <div className="entrance-install-banner">
          <div className="entrance-install-body">
            <span className="entrance-install-icon">📲</span>
            <div className="entrance-install-text">
              <strong>{t('installDukkanty')}</strong>
              {canPrompt && <p>{t('addToHomeScreen')}</p>}
              {showIosHint && (
                <p>
                  {t('iosTapThen')} <span className="entrance-ios-share">⬆</span> {t('iosAddToHomeScreen')}
                </p>
              )}
            </div>
          </div>
          <div className="entrance-install-actions">
            {canPrompt && (
              <button className="entrance-install-btn" onClick={promptInstall}>{t('install')}</button>
            )}
            <button className="entrance-install-dismiss" onClick={dismiss}>{t('notNow')}</button>
          </div>
        </div>
      )}

      {/* Hero subtitle */}
      <p className="entrance-subtitle">{t('subtitle')}</p>

      {/* Icon grid */}
      <div className="entrance-grid">
        {tileKeys
          .filter((tile) => !('requiresSuperuser' in tile && tile.requiresSuperuser) || user?.role === 'superuser')
          .map((tile) => (
          <button
            key={tile.id}
            className={`entrance-tile ${tile.requiresAuth && !user ? 'locked' : ''}`}
            onClick={() => handleTile(tile)}
          >
            <span className="entrance-tile-icon">{tile.icon}</span>
            <span className="entrance-tile-label">{t(tile.labelKey)}</span>
            {tile.requiresAuth && !user && <span className="entrance-tile-lock">🔒</span>}
          </button>
        ))}
      </div>

      {/* Personalized shop sections */}
      {!homeLoading && (
        <div className="entrance-discover">
          {recentShops.length > 0 && (
            <ShopCardCarousel
              title={t('recentlyVisited')}
              icon="🕐"
              shops={recentShops}
            />
          )}

          {/* My Wish Lists — between recent and recommendations */}
          {wishlists.length > 0 && (
            <section className="entrance-wishlists">
              <h2 className="entrance-wishlists-title">🛍️ {t('myWishlists')}</h2>
              <div className="entrance-wishlists-list">
                {wishlists.map((wl) => (
                  <div key={wl.token} className="entrance-wl-card" onClick={() => navigate(`/w/${wl.token}`)}>
                    <button
                      className="entrance-wl-remove"
                      onClick={(e) => { e.stopPropagation(); handleRemoveWishlist(wl.token) }}
                      aria-label="Remove"
                    >×</button>
                    <div className="entrance-wl-icon">
                      {wl.type === 'created' ? '📤' : '📥'}
                    </div>
                    <div className="entrance-wl-info">
                      <span className="entrance-wl-label">
                        {wl.multiShop
                          ? t('wishlistMultiShop')
                          : wl.shopName
                            ? t('wishlistFrom', { shopName: wl.shopName })
                            : t('myWishlists')}
                      </span>
                      <span className="entrance-wl-meta">
                        {t('wishlistItems', { count: wl.itemCount })}
                        {' · '}
                        {wl.type === 'created' ? t('wishlistCreated') : t('wishlistReceived')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {recommendations.length > 0 && (
            <ShopCardCarousel
              title={t('recommendedForYou')}
              icon="✨"
              shops={recommendations}
            />
          )}
          {trending.length > 0 && (
            <ShopCardCarousel
              title={t('trendingNow')}
              icon="🔥"
              shops={trending}
            />
          )}
        </div>
      )}

      {/* Browse all shops inline */}
      <BrowseShopsInline />
    </div>
  )
}
